import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Container,
  Alert,
  Button,
} from "@mui/material";
import { motion } from "framer-motion"; // Import framer-motion
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { AppState } from "../../store";
import { Reload, GetConfigs, GetLeaderBoard, GetTournamentList } from "../../store/user/actions";
import DashboardPanel from "./DashboardPanel";
import UsersPanel from "./UsersPanel";
import ConfigPanel from "./ConfigPanel";
import TournamentPanel from "./TournamentPanel";
import WorldsPanel from "./WorldsPanel";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const slideIn = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5 } },
};

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
      style={{ width: '100%' }}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `admin-tab-${index}`,
    'aria-controls': `admin-tabpanel-${index}`,
  };
}

const AdminPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const userModule = useSelector((state: AppState) => state.userModule);
  const { loginInfo, gameStatus } = userModule;
  const isLoading = gameStatus.loading;

  const [tabValue, setTabValue] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Check if the user is logged in
    if (!loginInfo.isLogin) {
      navigate('/login');
      return;
    }

    // Admin check - for now we'll use predefined admin accounts
    // In a real app, you would check if the user has admin privileges from your backend
    const adminAccounts = [
      "f4wqy.wam",
      "rwkwg.wam",
      "captxak12345",
      // "deadcitygame",
      // "a1b2c3.wam",
      // loginInfo.account // Temporarily allowing any logged in user to be an admin for testing
    ];

    const userIsAdmin = adminAccounts.includes(loginInfo.account);
    setIsAdmin(userIsAdmin);

    if (!userIsAdmin) {
      navigate('/');
      return;
    }

    // Load all necessary data for admin panels
    const initAdminData = async () => {
      await Promise.all([
        dispatch(Reload()),
        dispatch(GetConfigs()),
        dispatch(GetLeaderBoard()),
        dispatch(GetTournamentList())
      ]);
      setIsInitialized(true);
    };

    initAdminData();
  }, [loginInfo.isLogin, loginInfo.account, dispatch, navigate]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRefresh = () => {
    dispatch(Reload());
    dispatch(GetConfigs());
    dispatch(GetLeaderBoard());
    dispatch(GetTournamentList());
  };

  if (!isAdmin) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Alert severity="error">You don't have permission to access this page</Alert>
      </Box>
    );
  }

  if (isLoading || !isInitialized) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={60} sx={{ color: '#d9534f' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, px: { xs: 0, sm: 2, md: 4 } }}>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={slideIn}
      >
        <Box sx={{ margin: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AdminPanelSettingsIcon sx={{ fontSize: 40, mr: 2, color: '#d9534f' }} />
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'white' }}>
              WaxDeer Admin
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleRefresh}
            sx={{
              bgcolor: '#5cb85c',
              '&:hover': { bgcolor: '#449d44' },
              fontWeight: 'semibold',
            }}
          >
            Refresh Data
          </Button>
        </Box>
        <Box sx={{ width: '100%' }}>
            <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: '#23272b',
              borderRadius: 2,
              boxShadow: 2,
              mb: 2,
              px: 2,
            }}
            >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
              '& .MuiTabs-indicator': { backgroundColor: '#d9534f', height: 4, borderRadius: 2 },
              '& .MuiTab-root': {
                color: 'rgba(255,255,255,0.7)',
                fontWeight: 600,
                fontSize: 16,
                textTransform: 'none',
                minHeight: 48,
                px: 3,
                transition: 'color 0.2s',
                '&:hover': { color: '#fff', bgcolor: 'rgba(217,83,79,0.08)' },
              },
              '& .Mui-selected': { color: '#fff' },
              }}
            >
              <Tab label="Dashboard" {...a11yProps(0)} />
              <Tab label="Tournaments" {...a11yProps(3)} />
            </Tabs>
            </Box>
          <Box sx={{ width: '100%' }}>
            <TabPanel value={tabValue} index={0}>
              <DashboardPanel />
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              <TournamentPanel />
            </TabPanel>
          </Box>
        </Box>
      </motion.div>
    </Container>
  );
};

export default AdminPage;